/**
 * Diff a detected invoice suggestion against the current form values.
 *
 * Produces the list of fields the auto-fill would change, each classed as an
 * "add" (the field is currently empty) or a "change" (it already holds a
 * different value). The review dialog renders these rows; fields whose proposed
 * value equals the current one, or where the detector produced nothing, are
 * omitted.
 */
import type { Currency, PayeeId, SourceId } from "../api/types";
import type { InvoiceSuggestion } from "./invoice-detection";
import { SENDER_LABEL, type SenderKind } from "./invoice-sender";

export type AutofillFieldKey =
  | "name"
  | "sender"
  | "amount"
  | "amountMax"
  | "kid"
  | "payToAccount"
  | "dateReceived"
  | "dueDate"
  | "filename";

export type AutofillChangeKind = "add" | "change";

export interface AutofillChange {
  key: AutofillFieldKey;
  label: string;
  kind: AutofillChangeKind;
  /** Display of the current value; "" when the field is empty. */
  currentText: string;
  /** Display of the proposed value. */
  proposedText: string;
}

/**
 * The accepted (possibly edited) values the review dialog hands back. Only keys
 * the user kept are present; the form applies exactly those.
 */
export interface AutofillApply {
  name?: string;
  /** At most one of these: the sender the user kept, payee or account. */
  payeeId?: PayeeId;
  sourceId?: SourceId;
  amount?: string;
  /** Upper bound of the matching range; same currency as `amount`. */
  amountMax?: string;
  currency?: Currency;
  kid?: string;
  payToAccount?: string;
  /** ISO `yyyy-MM-dd`. */
  dateReceived?: string;
  /** ISO `yyyy-MM-dd`. */
  dueDate?: string;
  filename?: string;
}

/** Current form values relevant to auto-fill, already resolved for display. */
export interface AutofillCurrent {
  name: string;
  /** The form's current sender, resolved for display; null if none is set. */
  sender: { kind: SenderKind; name: string } | null;
  amount: string;
  /** Upper-bound amount currently in the form; "" if unset. */
  amountMax: string;
  currency: Currency;
  /** KID currently in the form; "" if unset. */
  kid: string;
  /** Pay-to account currently in the form; "" if unset. */
  payToAccount: string;
  /** ISO `yyyy-MM-dd`; "" if unset. */
  dateReceived: string;
  /** ISO `yyyy-MM-dd`; "" if unset. */
  dueDate: string;
  /** Filename of the target attachment; "" if none. */
  filename: string;
}

/** Detector dates are dotted (`YYYY.MM.DD`); the form uses ISO (`YYYY-MM-DD`). */
export const dottedDateToIso = (dotted: string): string =>
  dotted.replace(/\./g, "-");

const formatAmount = (amount: string, currency: string): string =>
  amount ? `${amount} ${currency}` : "";

/** The sender a suggestion names, as a kind plus the detector's name for it. */
export const suggestedSender = (
  suggestion: InvoiceSuggestion,
): { kind: SenderKind; name: string } =>
  suggestion.payee !== undefined
    ? { kind: "payee", name: suggestion.payee }
    : { kind: "source", name: suggestion.source };

export const computeAutofillChanges = (
  suggestion: InvoiceSuggestion,
  suggestedFilename: string,
  current: AutofillCurrent,
): AutofillChange[] => {
  const changes: AutofillChange[] = [];

  const consider = (
    key: AutofillFieldKey,
    label: string,
    currentText: string,
    proposedText: string,
  ): void => {
    // Nothing detected for this field, or it already matches — no row.
    if (proposedText === "" || proposedText === currentText) return;
    changes.push({
      key,
      label,
      kind: currentText === "" ? "add" : "change",
      currentText,
      proposedText,
    });
  };

  consider("name", "Name", current.name, suggestion.name);
  // The sender differs when either its name or its kind does: a payee and an
  // account that happen to share a name are still different senders.
  const sender = suggestedSender(suggestion);
  const currentSenderText = current.sender?.name ?? "";
  if (
    sender.name !== "" &&
    (sender.name !== currentSenderText || sender.kind !== current.sender?.kind)
  ) {
    changes.push({
      key: "sender",
      label: SENDER_LABEL[sender.kind],
      kind: currentSenderText === "" ? "add" : "change",
      currentText: currentSenderText,
      proposedText: sender.name,
    });
  }
  consider(
    "amount",
    "Amount",
    formatAmount(current.amount, current.currency),
    suggestion.amount
      ? formatAmount(suggestion.amount.amount, suggestion.amount.currency)
      : "",
  );
  consider(
    "amountMax",
    "Max amount",
    formatAmount(current.amountMax, current.currency),
    suggestion.amountMax
      ? formatAmount(suggestion.amountMax.amount, suggestion.amountMax.currency)
      : "",
  );
  consider("kid", "KID", current.kid, suggestion.kid ?? "");
  consider(
    "payToAccount",
    "Pay-to account",
    current.payToAccount,
    suggestion.payToAccount ?? "",
  );
  consider(
    "dateReceived",
    "Date received",
    current.dateReceived,
    dottedDateToIso(suggestion.dateReceived),
  );
  consider(
    "dueDate",
    "Due date",
    current.dueDate,
    dottedDateToIso(suggestion.dueDate),
  );
  consider("filename", "Filename", current.filename, suggestedFilename);

  return changes;
};
